<template>
  <div>
    <div
      v-if="loading"
      class="d-flex align-items-center justify-content-center py-3"
    >
      <b-spinner
        variant="primary"
        class="mr-1"
      />
      <span>Loading project shape schema...</span>
    </div>

    <b-card
      v-else
      :title="isEdit ? 'Edit Project Shape Schema' : 'Create Project Shape Schema'"
    >
      <b-form @submit.prevent="submit">
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Schema Name" label-for="schema-name">
              <b-form-input
                id="schema-name"
                v-model.trim="form.name"
                :state="nameState"
                placeholder="Enter schema name"
              />
              <small v-if="errors.name" class="text-danger">{{ errors.name }}</small>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6" class="d-flex align-items-center">
            <b-form-checkbox v-model="form.is_active" switch>
              {{ form.is_active ? 'Active' : 'Inactive' }}
            </b-form-checkbox>
          </b-col>
        </b-row>

        <hr>

        <small v-if="errors.attributes" class="text-danger d-block mb-1">{{ errors.attributes }}</small>

        

        <draggable
          v-model="form.attributes"
          handle=".shape-schema-attr-drag-handle"
          tag="div"
          class="shape-schema-attributes-draggable"
          @change="onAttributesDragChange"
        >
          <div
            v-for="(attr, index) in form.attributes"
            :key="attr._draggableKey"
            class="shape-schema-attribute-row border-bottom  bg-white"
          >
            <b-row class="align-items-start">
              <b-col cols="12" md="1" class="d-flex flex-column align-items-center justify-content-start pt-md-2 pb-2 pb-md-0">
                
                <span
                  class="shape-schema-attr-drag-handle cursor-move text-muted"
                  title="Drag to reorder"
                >
                   {{ index + 1 }}<feather-icon icon="MenuIcon" class="ml-1" />
                </span>
              </b-col>
              <b-col cols="12" md="4">
                <b-form-group label="Source" class="mb-md-0">
                  <b-form-select
                    v-model="attr.source"
                    :options="attributeOptions"
                    :state="fieldState(attributeErrors[index], 'source')"
                  />
                  <small v-if="attributeErrors[index] && attributeErrors[index].source" class="text-danger">
                    {{ attributeErrors[index].source }}
                  </small>
                </b-form-group>
              </b-col>

              <b-col cols="12" md="4">
                <b-form-group label="Output Name" class="mb-md-0">
                  <b-form-input
                    v-model.trim="attr.output_name"
                    :state="fieldState(attributeErrors[index], 'output_name')"
                    maxlength="50"
                  />
                  <small v-if="attributeErrors[index] && attributeErrors[index].output_name" class="text-danger">
                    {{ attributeErrors[index].output_name }}
                  </small>
                </b-form-group>
              </b-col>

              <!-- <b-col cols="12" md="3">
              <b-form-group label="Label (Optional)">
                <b-form-input v-model.trim="attr.label" />
              </b-form-group>
            </b-col> -->

              <b-col cols="12" md="2" class="d-flex align-items-center justify-content-md-end">
                <b-button
                  size="sm"
                  variant="outline-danger"
                  class="mt-md-1"
                  @click="removeAttribute(index)"
                >
                  <feather-icon icon="XIcon" />
                </b-button>
              </b-col>
            </b-row>
          </div>
        </draggable>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">Attributes</h5>
          <b-button size="sm" variant="outline-primary" @click="addAttribute">
            <feather-icon icon="PlusIcon" class="mr-50" />
            Add Row
          </b-button>
        </div>
        <div class="mt-2">
          <b-button type="submit" variant="primary" :disabled="submitting" class="mr-1">
            <b-spinner v-if="submitting" small class="mr-50" />
            {{ isEdit ? 'Update' : 'Create' }}
          </b-button>
          <b-button variant="outline-secondary" @click="$router.push({ name: 'settings-project-shape-schemas' })">
            Cancel
          </b-button>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BForm,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BFormSelect,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import projectShapeSchemasApi from '@/api/settings/projectShapeSchemas'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import draggable from 'vuedraggable'

let attributeRowKeySeq = 0
const nextAttributeRowKey = () => {
  attributeRowKeySeq += 1
  return `shape-schema-attr-${attributeRowKeySeq}`
}

const emptyAttribute = () => ({
  _draggableKey: nextAttributeRowKey(),
  source: '',
  output_name: '',
  label: '',
})

export default {
  name: 'ProjectShapeSchemaForm',
  components: {
    BCard,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormSelect,
    BButton,
    BSpinner,
    draggable,
  },
  data() {
    return {
      loading: false,
      submitting: false,
      initialSnapshot: '',
      availableAttributes: [],
      form: {
        name: '',
        is_active: true,
        attributes: [emptyAttribute()],
      },
      errors: {},
      attributeErrors: [],
    }
  },
  computed: {
    isEdit() {
      return Boolean(this.$route.params.id)
    },
    attributeOptions() {
      const options = [{ value: '', text: 'Select source attribute' }]
      this.availableAttributes.forEach(item => {
        options.push({ value: item.source, text: `${item.label} (${item.source})` })
      })
      return options
    },
    allowedSources() {
      return this.availableAttributes.map(item => item.source)
    },
    nameState() {
      if (!this.errors.name) return null
      return false
    },
    isDirty() {
      return this.initialSnapshot && this.initialSnapshot !== JSON.stringify(this.form)
    },
  },
  async mounted() {
    await this.init()
  },
  methods: {
    fieldState(errorObj, key) {
      if (!errorObj || !errorObj[key]) return null
      return false
    },
    addAttribute() {
      this.form.attributes.push(emptyAttribute())
    },
    removeAttribute(index) {
      this.form.attributes.splice(index, 1)
      if (this.attributeErrors.length === this.form.attributes.length + 1) {
        this.attributeErrors.splice(index, 1)
      }
      if (!this.form.attributes.length) this.form.attributes.push(emptyAttribute())
    },
    onAttributesDragChange(evt) {
      if (!evt.moved) return
      const { oldIndex, newIndex } = evt.moved
      if (
        this.attributeErrors.length !== this.form.attributes.length
        || oldIndex === undefined
        || newIndex === undefined
      ) return
      const row = this.attributeErrors.splice(oldIndex, 1)[0]
      this.attributeErrors.splice(newIndex, 0, row)
    },
    async init() {
      this.loading = true
      try {
        const attrsResponse = await projectShapeSchemasApi.getAvailableAttributes()
        this.availableAttributes = Array.isArray(attrsResponse.data) ? attrsResponse.data : (Array.isArray(attrsResponse) ? attrsResponse : [])

        if (this.isEdit) {
          const response = await projectShapeSchemasApi.get(this.$route.params.id)
          const schema = response.data || response
          this.form = {
            name: schema?.name || '',
            is_active: Boolean(schema?.is_active),
            attributes: Array.isArray(schema?.attributes) && schema.attributes.length
              ? schema.attributes.map(item => ({
                _draggableKey: nextAttributeRowKey(),
                source: item.source || '',
                output_name: item.output_name || '',
                label: item.label || '',
              }))
              : [emptyAttribute()],
          }
        }
      } catch (error) {
        this.showError(error?.response?.data?.message || error.message || 'Failed to load schema form')
      } finally {
        this.loading = false
        this.initialSnapshot = JSON.stringify(this.form)
      }
    },
    validate() {
      this.errors = {}
      this.attributeErrors = this.form.attributes.map(() => ({}))

      if (!this.form.name) {
        this.errors.name = 'Schema name is required'
      }

      if (!Array.isArray(this.form.attributes) || !this.form.attributes.length) {
        this.errors.attributes = 'At least one attribute row is required'
      } else {
        this.form.attributes.forEach((item, index) => {
          if (!item.source) {
            this.attributeErrors[index].source = 'Source is required'
          } else if (!this.allowedSources.includes(item.source)) {
            this.attributeErrors[index].source = 'Source must be selected from available attributes'
          }

          if (!item.output_name) {
            this.attributeErrors[index].output_name = 'Output name is required'
          } else if (item.output_name.length > 50) {
            this.attributeErrors[index].output_name = 'Output name must be at most 50 characters'
          }
        })
      }

      const hasAttributeErrors = this.attributeErrors.some(item => Object.keys(item).length > 0)
      return Object.keys(this.errors).length === 0 && !hasAttributeErrors
    },
    backendMessage(error) {
      return error?.response?.data?.message
        || (typeof error?.response?.data?.errors === 'string' ? error.response.data.errors : '')
        || error.message
    },
    applyBackendValidation(error) {
      const payload = error?.response?.data || {}
      const fieldErrors = payload.errors && typeof payload.errors === 'object' ? payload.errors : {}

      if (fieldErrors.name?.length) {
        this.errors.name = fieldErrors.name[0]
      } else if ((payload.message || '').toLowerCase().includes('name')) {
        this.errors.name = payload.message
      }

      if (fieldErrors.attributes?.length) {
        this.errors.attributes = fieldErrors.attributes[0]
      }
    },
    showSuccess(text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.$t('common.success'),
          icon: 'CheckIcon',
          variant: 'success',
          text,
        },
      })
    },
    showError(text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.$t('common.error'),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
          text,
        },
      })
    },
    async submit() {
      if (!this.validate()) return

      this.submitting = true
      const payload = {
        name: this.form.name,
        is_active: Boolean(this.form.is_active),
        attributes: this.form.attributes.map(item => ({
          source: item.source,
          output_name: item.output_name,
          label: item.label || null,
        })),
      }

      try {
        if (this.isEdit) {
          await projectShapeSchemasApi.update(this.$route.params.id, payload)
        } else {
          await projectShapeSchemasApi.create(payload)
        }
        this.showSuccess(this.isEdit ? 'Shape schema updated successfully' : 'Shape schema created successfully')
        this.initialSnapshot = JSON.stringify(this.form)
        this.$router.push({ name: 'settings-project-shape-schemas' })
      } catch (error) {
        this.applyBackendValidation(error)
        this.showError(this.backendMessage(error) || 'Failed to save shape schema')
      } finally {
        this.submitting = false
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    if (!this.isDirty) {
      next()
      return
    }
    if (window.confirm('You have unsaved changes. Leave this page?')) {
      next()
    } else {
      next(false)
    }
  },
}
</script>
